//Stores
import { writable } from 'svelte/store';
//Services
import { fetchNotificationLog, updateNotificationState } from 'src/services/notification-log';
//Types
import type { NotificationLogEntry } from 'src/types/NotificationLog';
import { NotificationLogState } from 'src/types/NotificationLog';

/**
 * Server-backed store for the backend `notification-log`.
 *
 * Unlike the localStorage-based `read_notifications` / `dismissed_notifications`
 * stores used for client-computed device alerts, read/dismissed state here lives
 * on the backend and is the source of truth (via the update-state endpoint).
 */
const create_notification_log = () => {
	const { subscribe, set, update } = writable<NotificationLogEntry[]>([]);

	/** Fetch the latest notifications and replace the store contents. */
	const load = async (): Promise<NotificationLogEntry[]> => {
		const entries = await fetchNotificationLog();
		set(entries);
		return entries;
	};

	/**
	 * Persist a new state for one or more notifications and reflect the backend
	 * response locally. No-op on failure (store is left untouched).
	 */
	const setState = async (rowIds: number[], state: NotificationLogState) => {
		const updated = await updateNotificationState(rowIds, state);
		if (updated) {
			const updatedById = new Map(updated.map((entry) => [entry.id, entry]));
			update((list) =>
				list.map((entry) => {
					const updatedEntry = updatedById.get(entry.id);
					return updatedEntry ? { ...entry, ...updatedEntry } : entry;
				}),
			);
		}
		return updated;
	};

	const normalizeRowIds = (rowIds: number | number[]): number[] =>
		Array.isArray(rowIds) ? rowIds : [rowIds];

	return {
		subscribe,
		set,
		load,
		setState,
		markSeen: (rowIds: number | number[]) =>
			setState(normalizeRowIds(rowIds), NotificationLogState.Seen),
		markUnseen: (rowIds: number | number[]) =>
			setState(normalizeRowIds(rowIds), NotificationLogState.Unseen),
		dismiss: (rowIds: number | number[]) =>
			setState(normalizeRowIds(rowIds), NotificationLogState.Dismissed),
		reset: () => set([]),
	};
};

export const notification_log = create_notification_log();
